/**
 * 命令仓库 - 处理命令的持久化
 */

import { db, type CommandRecord } from './schema';
import type { CadCommand } from '@puppycad/cad-core';


/**
 * 命令仓库类
 */
export class CommandRepository {
    /**
     * 获取项目的所有命令
     */
    async getProjectCommands(projectId: string): Promise<CadCommand[]> {
        try {
            const records = await db.commands
                .where('projectId')
                .equals(projectId)
                .sortBy('seqId');

            const commands = records.map(r => r.command);
            //------ TypeCheck日志修复 开始------
            console.debug(`获取项目 ${projectId} 的命令，共 ${commands.length} 条`);
            //------ TypeCheck日志修复 结束------

            return commands;
        } catch (error) {
            console.log(`获取项目命令失败: ${projectId}`, error);
            throw error;
        }
    }

    /**
     * 添加命令
     */
    async addCommand(projectId: string, command: CadCommand): Promise<void> {
        try {
            // 获取当前最大序号
            const lastCommand = await db.commands
                .where('projectId')
                .equals(projectId)
                .last();

            const seqId = (lastCommand?.seqId ?? -1) + 1;

            const record: CommandRecord = {
                id: command.id,
                projectId,
                command,
                seqId
            };

            await db.commands.add(record);
            //------ TypeCheck日志修复 开始------
            console.debug(`添加命令: ${command.type} (seqId=${seqId})`);
            //------ TypeCheck日志修复 结束------
        } catch (error) {
            console.log('添加命令失败', error);
            throw error;
        }
    }

    /**
     * 批量添加命令
     */
    async bulkAddCommands(projectId: string, commands: CadCommand[]): Promise<void> {
        try {
            // 获取当前最大序号
            const lastCommand = await db.commands
                .where('projectId')
                .equals(projectId)
                .last();

            let seqId = (lastCommand?.seqId ?? -1) + 1;

            const records: CommandRecord[] = commands.map(command => ({
                id: command.id,
                projectId,
                command,
                seqId: seqId++
            }));

            await db.commands.bulkAdd(records);
        } catch (error) {
            console.log('批量添加命令失败', error);
            throw error;
        }
    }

    /**
     * 获取指定范围的命令
     */
    async getCommandRange(
        projectId: string,
        startSeqId: number,
        endSeqId: number
    ): Promise<CadCommand[]> {
        try {
            const records = await db.commands
                .where('[projectId+seqId]')
                .between([projectId, startSeqId], [projectId, endSeqId], true, true)
                .toArray();

            return records.map(r => r.command);
        } catch (error) {
            console.log('获取命令范围失败', error);
            throw error;
        }
    }

    /**
     * 删除项目的所有命令
     */
    async clearProjectCommands(projectId: string): Promise<void> {
        try {
            const count = await db.commands
                .where('projectId')
                .equals(projectId)
                .delete();

        } catch (error) {
            console.log(`清除项目命令失败: ${projectId}`, error);
            throw error;
        }
    }

    /**
     * 获取命令数量
     */
    async getCommandCount(projectId: string): Promise<number> {
        try {
            return await db.commands
                .where('projectId')
                .equals(projectId)
                .count();
        } catch (error) {
            console.log('获取命令数量失败', error);
            throw error;
        }
    }

    /**
     * 获取最后 N 条命令
     */
    async getLastCommands(projectId: string, count: number): Promise<CadCommand[]> {
        try {
            const records = await db.commands
                .where('projectId')
                .equals(projectId)
                .reverse()
                .limit(count)
                .toArray();

            return records.map(r => r.command).reverse();
        } catch (error) {
            console.log('获取最后 N 条命令失败', error);
            throw error;
        }
    }

    /**
     * 保存项目完整状态（事务）
     */
    async saveProjectState(
        projectId: string,
        commands: CadCommand[]
    ): Promise<void> {
        try {
            await db.transaction('rw', [db.commands], async () => {
                // 清空旧命令
                await this.clearProjectCommands(projectId);

                // 保存新命令
                await this.bulkAddCommands(projectId, commands);
            });

        } catch (error) {
            console.log('保存项目状态失败', error);
            throw error;
        }
    }
}

// 导出单例
export const commandRepo = new CommandRepository();
export default commandRepo;
